import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getContractUsageRequest } from "Api/billingSystem";

import type { RootState } from "Store/configureStore";
import type { ContractUsage } from "Api/billingSystem/types";

type GetContractUsageArgs = {
  billingProfileId: string;
  contractId: string;
};

export const getContractUsage = createAppAsyncThunk(
  "billingSystem/contractUsage/getByContract",
  async (
    { billingProfileId, contractId }: GetContractUsageArgs,
    { rejectWithValue }
  ) => {
    try {
      return await getContractUsageRequest(billingProfileId, contractId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

type ContractUsageState = {
  byProfileId: Record<
    string,
    Record<
      string,
      {
        loading: boolean;
        data?: ContractUsage;
        error?: string;
      }
    >
  >;
};

const initialState: ContractUsageState = {
  byProfileId: {}
};

const contractUsageSlice = createSlice({
  name: "contractUsage",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getContractUsage.pending, (state, { meta }) => {
        const { billingProfileId, contractId } = meta.arg;
        setDeep(
          state,
          ["byProfileId", billingProfileId, contractId, "loading"],
          true
        );
        setDeep(
          state,
          ["byProfileId", billingProfileId, contractId, "error"],
          undefined
        );
      })
      .addCase(getContractUsage.fulfilled, (state, action) => {
        const { billingProfileId, contractId } = action.meta.arg;
        setDeep(
          state,
          ["byProfileId", billingProfileId, contractId, "loading"],
          false
        );
        setDeep(
          state,
          ["byProfileId", billingProfileId, contractId, "data"],
          action.payload
        );
      })
      .addCase(getContractUsage.rejected, (state, action) => {
        const { billingProfileId, contractId } = action.meta.arg;
        setDeep(
          state,
          ["byProfileId", billingProfileId, contractId, "loading"],
          false
        );
        setDeep(
          state,
          ["byProfileId", billingProfileId, contractId, "error"],
          action.payload?.error
        );
      });
  }
});

export default contractUsageSlice.reducer;

const contractUsageStateSelector = (state: RootState) => state.contractUsage;

export const selectContractUsage = createSelector(
  contractUsageStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (_: RootState, __: string, contractId: string) => contractId,
  (state, billingProfileId, contractId) =>
    state.byProfileId[billingProfileId]?.[contractId]?.data
);

export const selectContractUsageLoading = createSelector(
  contractUsageStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (_: RootState, __: string, contractId: string) => contractId,
  (state, billingProfileId, contractId) =>
    state.byProfileId[billingProfileId]?.[contractId]?.loading ?? false
);

export const selectContractUsageError = createSelector(
  contractUsageStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (_: RootState, __: string, contractId: string) => contractId,
  (state, billingProfileId, contractId) =>
    state.byProfileId[billingProfileId]?.[contractId]?.error
);

export const selectContractUsageVouchers = createSelector(
  selectContractUsage,
  data => data?.vouchers
);
